import {
  AutoSeatSelectionPayload,
  CancelCartPayload,
  GetInventoryApiPayload,
} from "@/types/api.types";
import { TeamConfig } from "../hooks/useTeamConfig";
import { CartManagerState } from "../store/data/purchaseSeats/cartManager";
import { throttleTime } from "../utils/utils";
import { findAllPerksLocations } from "@/shared/modules/PurchaseSeats/utils.perks";
import { inventoryTypeUIDs, vendorUUIDs } from "../config/constants";
import {
  DynamicSeatCount,
  EventType,
  InventoryFilters,
} from "../store/data/types";
import { findUnholdEventsInCart } from "@/shared/modules/PurchaseSeats/utils.cart";

export async function retryApiRequest<T extends Record<string, any>>(
  fetcher: Function,
  config: {
    payload: T;
    status: number;
    delay: number;
    nextReqDataKey?: string;
    retryLimit: number;
  },
  count = 0
) {
  const res = await fetcher(config.payload);
  count++;
  if (res?.status == config.status && count < config.retryLimit) {
    await throttleTime(config.delay);
    const nextReqDataKey = config.nextReqDataKey as keyof T;
    const payloadToAppend = res?.data?.[nextReqDataKey];
    if (payloadToAppend) config.payload[nextReqDataKey] = payloadToAppend;
    return retryApiRequest(fetcher, config, count);
  } else {
    return res;
  }
}

export function createCancelCartPayload(args: {
  seatLocations?: string[];
  perkLocations?: string[];
  serviceFeeLocations?: string[];
  cartState?: CartManagerState;
  teamConfig: TeamConfig;
  offerId?: number;
}) {
  let payload = {} as CancelCartPayload;
  payload.teamSchema = args?.teamConfig.teamSchema;
  payload.cartId = args?.cartState?.cartId;
  payload.seatLocations = args?.seatLocations || [];

  if (args.perkLocations) {
    payload.perkLocations = args.perkLocations;
  }

  // tm specific properties
  if (args?.teamConfig?.platform == vendorUUIDs.ticketmaster) {
    payload.serviceFeeLocations = args?.serviceFeeLocations;
  }

  // tdc specific properties
  if (args?.teamConfig?.platform == vendorUUIDs.tickesdotcom) {
    payload.tdcCancelRequest = {
      ifMatch: args?.cartState?.sessionId,
      offerId: args?.offerId,
    };
  }

  return payload;
}

export function createAutoSeatSelectionPayload(args: {
  teamConfig: TeamConfig;
  selectedEvents: EventType[];
  cartState: CartManagerState;
  filterSeatCount: number;
}) {
  const unHoldEvents = findUnholdEventsInCart(
    args.selectedEvents,
    args.cartState
  );
  let payload = {} as AutoSeatSelectionPayload;
  payload.teamSchema = args?.teamConfig.teamSchema;
  payload.cart = args?.cartState?.cart;
  payload.eventCodes = unHoldEvents.map((evt) => evt.eventCode);
  payload.cartId = args?.cartState?.cartId;
  payload.sessionId = args?.cartState?.sessionId;

  return payload;
}

export function createGetInvetoryApiPayload(args: {
  activeEvent: EventType | null;
  teamConfig: TeamConfig;
  inventoryFilters: InventoryFilters;
}) {
  const { inventoryType, ...inventoryFilters } = args.inventoryFilters;
  let payload = {} as GetInventoryApiPayload;
  payload.teamSchema = args?.teamConfig?.teamSchema;
  payload.eventCode = args?.activeEvent?.eventCode || "";
  payload.inventoryType = inventoryType || "";
  payload.inventoryFilters = inventoryFilters;

  if (payload.inventoryType == inventoryTypeUIDs.suitLevel) {
    payload.inventoryFilters.priceRange = [null, null] as any;
  }

  return payload;
}

export function createDynamicSeatCountPayload(args: {
  teamConfig: TeamConfig;
  seatCountArr: DynamicSeatCount[];
}) {
  const cfg = args?.teamConfig?.uiConfigs?.gameFiltersSettings?.dynamicSeatCount; // prettier-ignore
  if (!cfg?.length) return [];

  return args?.seatCountArr
    ?.map((itm) => {
      const cfgItem = cfg?.find((sc) => sc.id === itm.id) as (typeof cfg)[0];
      return {
        ticketTypeCode: cfgItem?.ticketTypeCode,
        pricingLabel: cfgItem?.pricingLabel,
        orderIndex: cfgItem?.orderIndex,
        seatCount: itm?.seatCount,
      };
    })
    ?.filter((itm) => itm?.seatCount > 0);
}
